// Marketing landing page — the first thing a visitor (or judge) sees.
// Pure presentation; "Launch workspace" routes into the actual tool.
const TOOLS = [
  {
    name: "import_from_naukri",
    blurb: "Start from a Naukri search; the agent harvests the companies for you.",
  },
  {
    name: "discover_company_jobs",
    blurb: "Resolves a company's official career portal and pulls the real roles.",
  },
  {
    name: "match_with_resume",
    blurb: "Scores each role against your resume and drafts tailored bullet rewrites.",
  },
  {
    name: "add_job_to_board",
    blurb: "Drops a specific opening onto your pipeline board.",
  },
  {
    name: "list_board_jobs",
    blurb: "Reads the board so the agent knows what you're tracking.",
  },
];

const STEPS = [
  {
    n: "01",
    title: "Start with a company or a Naukri search",
    body: "Name a company, or paste the companies you see on a Naukri results page. Type it yourself, or ask the in-browser agent.",
  },
  {
    n: "02",
    title: "The agent goes to the official source",
    body: "For each company it resolves the real career portal — skipping aggregators that show only a fraction of the roles — and extracts every live opening.",
  },
  {
    n: "03",
    title: "Matched to your resume, on one board",
    body: "Openings scores each role against your resume and proposes tailored bullet rewrites. You review; the agent does the legwork.",
  },
];

function Star({ className = "" }: { className?: string }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" className={className}>
      <path
        d="M12 1.4l2.1 8.5 8.5 2.1-8.5 2.1L12 22.6l-2.1-8.5L1.4 12l8.5-2.1z"
        fill="currentColor"
      />
    </svg>
  );
}

export function Landing({ onLaunch }: { onLaunch: () => void }) {
  return (
    <div className="min-h-screen overflow-y-auto">
      {/* ── Nav ─────────────────────────────────────────────────────────── */}
      <nav className="sticky top-0 z-20 border-b border-border/60 bg-ink/80 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3.5 sm:px-8">
          <div className="flex items-center gap-2.5">
            <span className="grid h-9 w-9 place-items-center rounded-lg border border-accent/25 bg-accent/10 text-accent">
              <Star />
            </span>
            <span className="wordmark text-[19px] text-text">Openings</span>
          </div>
          <div className="flex items-center gap-6">
            <a href="#how" className="hidden text-sm text-muted transition-colors hover:text-text sm:block">
              How it works
            </a>
            <a href="#tools" className="hidden text-sm text-muted transition-colors hover:text-text sm:block">
              WebMCP tools
            </a>
            <a
              href="https://github.com/Priyanka2-ui/Openings"
              target="_blank"
              rel="noreferrer noopener"
              className="hidden text-sm text-muted transition-colors hover:text-text sm:block"
            >
              GitHub
            </a>
            <button onClick={onLaunch} className="btn-primary px-4 py-2 text-sm">
              Launch workspace
            </button>
          </div>
        </div>
      </nav>

      {/* ── Hero ────────────────────────────────────────────────────────── */}
      <header className="relative mx-auto max-w-6xl px-5 pb-20 pt-16 sm:px-8 sm:pt-24">
        <div className="mx-auto max-w-3xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-border bg-surface-2/50 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.22em] text-faint">
            <Star className="h-3 w-3 text-accent" />
            Built on WebMCP
          </span>
          <h1 className="mt-6 font-display text-4xl font-semibold leading-[1.05] tracking-tight text-text sm:text-6xl">
            Skip the aggregators.
            <br />
            Go straight to the{" "}
            <span className="relative whitespace-nowrap text-accent">source.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-muted sm:text-lg">
            Openings is a job workspace where you and an in-browser AI agent work
            the same board. The agent finds each company's official career portal,
            pulls the real openings, and scores them against your resume — live,
            on one page.
          </p>
          <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <button onClick={onLaunch} className="btn-primary px-6 py-3 text-base">
              <Star className="h-4 w-4" />
              Launch the workspace
            </button>
            <a href="#how" className="btn-ghost px-6 py-3 text-base">
              See how it works
            </a>
          </div>
          <p className="mt-4 font-mono text-[11px] text-faint">
            No sign-up. Open it in a WebMCP browser and the agent takes the wheel.
          </p>
        </div>

        {/* Hero visual — a stylized live-tool panel */}
        <div className="mx-auto mt-16 max-w-3xl">
          <div className="panel overflow-hidden p-0">
            <div className="flex items-center gap-2 border-b border-border px-4 py-2.5">
              <span className="h-2.5 w-2.5 rounded-full bg-rose/70" />
              <span className="h-2.5 w-2.5 rounded-full bg-amber/70" />
              <span className="h-2.5 w-2.5 rounded-full bg-signal/70" />
              <span className="ml-2 font-mono text-[11px] text-faint">
                openings-webmcp.netlify.app
              </span>
              <span className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-signal/30 bg-signal-soft/50 px-2 py-0.5 font-mono text-[10px] text-signal">
                <span className="h-1.5 w-1.5 rounded-full bg-signal" />
                WebMCP · 5 tools
              </span>
            </div>
            <div className="space-y-2.5 p-4 font-mono text-[12px] leading-relaxed">
              <p className="text-muted">
                <span className="text-accent">agent</span> › import_from_naukri
                <span className="text-faint"> ({"{"} roleQuery: "AI Engineer" {"}"})</span>
              </p>
              <p className="text-faint">→ discovered 4 companies from Naukri</p>
              <p className="text-muted">
                <span className="text-accent">agent</span> › discover_company_jobs
                <span className="text-faint"> ("Stripe")</span>
              </p>
              <p className="text-signal">
                ✓ 15 live openings from boards.greenhouse.io/stripe — direct links
              </p>
              <p className="text-muted">
                <span className="text-accent">agent</span> › match_with_resume
                <span className="text-faint"> (jobId)</span>
              </p>
              <p className="text-signal">✓ 82% fit · 5 tailored bullet rewrites</p>
            </div>
          </div>
        </div>
      </header>

      {/* ── How it works ────────────────────────────────────────────────── */}
      <section id="how" className="border-t border-border/60 bg-surface/20 py-20">
        <div className="mx-auto max-w-6xl px-5 sm:px-8">
          <div className="max-w-2xl">
            <p className="eyebrow">How it works</p>
            <h2 className="mt-2 font-display text-3xl font-semibold tracking-tight text-text sm:text-4xl">
              The agent does the breadth. You keep the judgment.
            </h2>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {STEPS.map((s) => (
              <div key={s.n} className="panel p-6">
                <span className="font-mono text-sm text-accent">{s.n}</span>
                <h3 className="mt-3 font-display text-lg font-medium text-text">
                  {s.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-muted">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WebMCP tools ────────────────────────────────────────────────── */}
      <section id="tools" className="py-20">
        <div className="mx-auto max-w-6xl px-5 sm:px-8">
          <div className="max-w-2xl">
            <p className="eyebrow">The interface</p>
            <h2 className="mt-2 font-display text-3xl font-semibold tracking-tight text-text sm:text-4xl">
              Five WebMCP tools, same code as the buttons.
            </h2>
            <p className="mt-4 text-base leading-relaxed text-muted">
              Every tool the agent calls is the exact function a human triggers in
              the UI, registered on{" "}
              <code className="rounded bg-surface-2 px-1.5 py-0.5 font-mono text-[13px] text-accent">
                document.modelContext
              </code>
              . Nothing is demo-only.
            </p>
          </div>
          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {TOOLS.map((t) => (
              <div key={t.name} className="panel flex flex-col gap-2 p-5">
                <code className="w-fit rounded-md border border-border bg-ink px-2 py-1 font-mono text-[12px] text-accent">
                  {t.name}
                </code>
                <p className="text-sm leading-relaxed text-muted">{t.blurb}</p>
              </div>
            ))}
            <div className="flex flex-col justify-center gap-3 rounded-2xl border border-dashed border-border p-5">
              <p className="text-sm leading-relaxed text-muted">
                Ready to see it live?
              </p>
              <button onClick={onLaunch} className="btn-primary w-fit px-4 py-2 text-sm">
                Launch the workspace
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ─────────────────────────────────────────────────────────── */}
      <section className="border-t border-border/60 bg-surface/20 py-20">
        <div className="mx-auto max-w-3xl px-5 text-center sm:px-8">
          <Star className="mx-auto h-8 w-8 text-accent" />
          <h2 className="mt-5 font-display text-3xl font-semibold tracking-tight text-text sm:text-4xl">
            A better way to find the roles that are actually there.
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-muted">
            Aggregators show a fraction and hide the link. Openings and its agent
            go to the real source — and do it for twenty companies in the time it
            takes you to open one tab.
          </p>
          <div className="mt-8">
            <button onClick={onLaunch} className="btn-primary px-6 py-3 text-base">
              Launch the workspace
            </button>
          </div>
        </div>
      </section>

      {/* ── Footer ──────────────────────────────────────────────────────── */}
      <footer className="border-t border-border/60 py-8">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-3 px-5 sm:flex-row sm:px-8">
          <div className="flex items-center gap-2 text-muted">
            <Star className="h-4 w-4 text-accent" />
            <span className="wordmark text-base text-text">Openings</span>
            <span className="text-faint">· Built for the WebMCP Challenge</span>
          </div>
          <a
            href="https://github.com/Priyanka2-ui/Openings"
            target="_blank"
            rel="noreferrer noopener"
            className="font-mono text-[12px] text-faint transition-colors hover:text-text"
          >
            github.com/Priyanka2-ui/Openings ↗
          </a>
        </div>
      </footer>
    </div>
  );
}
